import React from "react";
import { useTranslation } from "react-i18next";
import { formatTimestamp } from "../../utils/utils";
import { formatMetric, gradeOf, interfaceLabel, qualityColor } from "../../utils/netquality";
import styles from "./probe_result.module.css";

// Lee una clave de la muestra tal como la manda el equipo (todo arriba) o
// como la guarda la nube (lo que no es columna va en `details`).
function pick(sample, key) {
  if (sample[key] !== undefined && sample[key] !== null) return sample[key];
  return sample.details ? sample.details[key] : undefined;
}

function signalText(sample) {
  const iface = sample.interface;
  if (iface === "wifi") {
    const dbm = sample.signal_dbm;
    const ssid = sample.ssid;
    if (dbm === null || dbm === undefined) return "—";
    return `${Math.round(dbm)} dBm${ssid ? ` · ${ssid}` : ""}`;
  }
  if (iface === "lte") {
    const parts = [];
    if (sample.rsrp !== null && sample.rsrp !== undefined) parts.push(`RSRP ${Math.round(sample.rsrp)} dBm`);
    else if (sample.signal_dbm !== null && sample.signal_dbm !== undefined) parts.push(`${Math.round(sample.signal_dbm)} dBm`);
    else if (sample.signal_pct !== null && sample.signal_pct !== undefined) parts.push(`${Math.round(sample.signal_pct)} %`);
    if (sample.sinr !== null && sample.sinr !== undefined) parts.push(`SINR ${Math.round(sample.sinr)} dB`);
    const tech = [sample.access_tech, sample.operator].filter(Boolean).join(" ");
    if (tech) parts.push(tech.toUpperCase());
    return parts.length ? parts.join(" · ") : "—";
  }
  const speed = pick(sample, "link_speed_mbps");
  return speed ? `${speed} Mbps link` : "—";
}

export function QualityBadge({ score }) {
  const { t } = useTranslation();
  if (score === null || score === undefined) {
    return <span className={styles.badge} style={{ background: "#9ca3af" }}>{t("netmap.quality.unknown")}</span>;
  }
  return (
    <span className={styles.badge} style={{ background: qualityColor(score) }}>
      {Math.round(score)} · {gradeOf(score)}
    </span>
  );
}

export default function ProbeResult({ samples, showTime = false, compact = false }) {
  const { t } = useTranslation();
  if (!samples || samples.length === 0) return null;
  return (
    <div className={styles.wrapper}>
      <table className={`${styles.table} ${compact ? styles.compact : ""}`}>
        <thead>
          <tr>
            <th>{t("netquality.table.interface")}</th>
            <th>{t("netquality.table.status")}</th>
            <th>{t("netquality.table.signal")}</th>
            <th>{t("netquality.table.latency")}</th>
            <th>{t("netquality.table.jitter")}</th>
            <th>{t("netquality.table.loss")}</th>
            <th>{t("netquality.table.down")}</th>
            <th>{t("netquality.table.up")}</th>
            <th>{t("netquality.table.quality")}</th>
            {showTime && <th>{t("netquality.table.when")}</th>}
          </tr>
        </thead>
        <tbody>
          {samples.map((s, i) => {
            const modemMissing = s.interface === "lte" && pick(s, "modem_present") === false;
            const status = modemMissing
              ? t("netquality.table.noModem")
              : s.connected
                ? `${t("netquality.table.connected")}${s.is_default_route ? ` · ${t("netquality.table.defaultRoute")}` : ""}`
                : t("netquality.table.notConnected");
            const binding = pick(s, "binding");
            return (
              // data-label: en móvil la fila se apila como tarjeta y cada
              // celda muestra su propio encabezado (probe_result.module.css).
              <tr key={s.id || `${s.interface}-${i}`}>
                <td className={styles.iface} data-label={t("netquality.table.interface")}>{interfaceLabel(s.interface)}</td>
                <td className={styles.status} data-label={t("netquality.table.status")}>
                  <span className={s.connected ? styles.ok : styles.off}>{status}</span>
                  {binding === "address" && <div className={styles.note}>{t("netquality.table.notBound")}</div>}
                  {s.error && <div className={styles.error}>{s.error}</div>}
                </td>
                <td className={styles.signal} data-label={t("netquality.table.signal")}>{signalText(s)}</td>
                <td data-label={t("netquality.table.latency")}>{formatMetric("rtt_avg_ms", s.rtt_avg_ms)}</td>
                <td data-label={t("netquality.table.jitter")}>{formatMetric("jitter_ms", s.jitter_ms)}</td>
                <td data-label={t("netquality.table.loss")}>{formatMetric("packet_loss_pct", s.packet_loss_pct)}</td>
                <td data-label={t("netquality.table.down")}>{formatMetric("down_mbps", s.down_mbps)}</td>
                <td data-label={t("netquality.table.up")}>{formatMetric("up_mbps", s.up_mbps)}</td>
                <td className={styles.quality} data-label={t("netquality.table.quality")}><QualityBadge score={s.score} /></td>
                {showTime && (
                  <td className={styles.when} data-label={t("netquality.table.when")}>
                    {formatTimestamp(s.created_at || s.measured_at)}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
